import { Button, Group, Modal } from '@mantine/core';
import React, { useRef, useState } from 'react';

import { AutoFormik } from '@portals/autoformik';
import {
  FieldTypeEnum,
  SupportedCommandFieldType,
  SupportedCommandType,
} from '@portals/types';

import { CommandFeatures } from './CommandFeatures';
import CustomFields from './CustomFields';

const FIELDS: Array<SupportedCommandFieldType> = [
  {
    name: 'friendly_name',
    title: 'Friendly name',
    placeholder: 'The name the user will see',
    required: true,
  },
  {
    name: 'description',
    title: 'Description',
    placeholder: 'Detailed description for the user',
    required: true,
  },
  {
    name: 'name',
    title: 'Name sent to device',
    placeholder: 'The name the device will get when this command is sent',
    required: true,
  },
  {
    name: 'with_file',
    title: 'Requires file',
    type: FieldTypeEnum.Checkbox,
    default: false,
  },
  {
    name: 'file_type',
    title: 'Required file type',
    placeholder: 'firmware',
    hidden: ({ with_file }) => !with_file,
  },
  {
    name: 'timeout_in_seconds',
    title: 'Timeout (in seconds)',
    placeholder: 'Maximum possible run time for command',
    type: FieldTypeEnum.Number,
    inputProps: {
      min: 0,
    },
  },
  {
    name: 'allow_duplicate_commands',
    title: 'Allow sending multiple commands with identical parameters',
    type: FieldTypeEnum.Checkbox,
    default: false,
  },
  {
    name: 'command_features',
    type: FieldTypeEnum.Custom,
    default: [],
    component: CommandFeatures,
  },
  {
    name: 'active',
    title: 'Active',
    type: FieldTypeEnum.Checkbox,
    default: true,
  },
  {
    name: 'is_visible_in_panel',
    title: 'Show commands in panel',
    type: FieldTypeEnum.Checkbox,
    default: true,
  },
  {
    name: 'require_user_approval',
    title: 'Requires additional confirmation',
    type: FieldTypeEnum.Checkbox,
    default: false,
  },
];

interface SupportedCommandFormProps {
  initialValues?: Partial<SupportedCommandType>;
  isEdit: boolean;
  isLoading: boolean;
  errors?: string;
  closeMe: () => void;
  handleUpdate: (data: Record<string, any>) => void;
}

export function SupportedCommandForm({
  isEdit = false,
  isLoading,
  initialValues = {},
  errors,
  closeMe,
  handleUpdate,
}: SupportedCommandFormProps) {
  const formRef = useRef(null);

  const [command, setCommand] =
    useState<Partial<SupportedCommandType>>(initialValues);

  const onSubmit = () => {
    handleUpdate(command);
  };

  return (
    <Modal
      opened
      size="xl"
      onClose={closeMe}
      title={isEdit ? 'Edit supported command' : 'Add supported command'}
      sx={(theme) => ({
        '.modal-body': {
          padding: 0,
          marginBottom: theme.spacing.md,
        },
        '.modal-footer': {
          paddingRight: 0,
          paddingLeft: 0,
        },
      })}
    >
      <AutoFormik
        key="add-command"
        fields={FIELDS}
        innerRef={formRef}
        initialValues={initialValues}
        handleSubmit={onSubmit}
        modal={closeMe}
        serverError={errors}
        inProgress={isLoading}
        actionWrap={() => null}
        formikProps={{ validateOnMount: true }}
        onChange={(values) => {
          setCommand((curr) => ({
            ...curr,
            ...values,
          }));
        }}
      />

      <CustomFields command={command} onChange={setCommand} />

      <Group position="right">
        <Button
          variant="default"
          data-testid="create-command-cancel-button"
          onClick={closeMe}
        >
          Cancel
        </Button>

        <Button
          disabled={!formRef.current?.isValid}
          data-testid="create-command-submit-button"
          onClick={onSubmit}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </Group>
    </Modal>
  );
}
